import { PageView, useRestoreSavedView } from '@ecosystem/newron-client-lib';
import { Context, Entity, FiltersValues, Params, SettingsValues } from '../types';
import { PageSettingsManager } from '../page/settings';
import { useEffect, useRef, useState } from 'react';
import { CorePageState } from '../page/reducer';
import { ContextManager } from '../context/manager';
import { InsightCollectionDataType } from '../content/collection/types';
import { PageSettings } from '../page/types';

const SAVED_VIEW_KEY = 'savedViewKey_marketPortfolio';
export type SavedViewType = { version: SavedViewVersion; dataType?: InsightCollectionDataType };

export enum SavedViewVersion {
  v0 = '0',
  v1 = '1',
}

export type UseRestoreSavedViewV1 = SavedViewType & {
  settingValues: SettingsValues;
  filterValues: FiltersValues;
  pageSettings: Record<string, Params>;
};

export type UseRestoreSavedViewV0 = SavedViewType & {
  context: Context;
  pageSettings: PageSettings;
};

const clearValues = (values: Record<string, Entity[]>) => Object.fromEntries(Object.keys(values).map(key => [key, []]));

export const useSavedView = ({ page, contextLoading }: Pick<CorePageState, 'contextLoading' | 'page'>) => {
  const [restoreSavedView, setRestoreSavedView] = useState<SavedViewType>();
  const [isRestoringSavedView, setIsRestoringSavedView] = useState<boolean>(false);
  const isRestoreSavedViewApplied = useRef(false);
  const [restoreLoading, setRestoreLoading] = useState(true);

  const savedView = window.sessionStorage.getItem(SAVED_VIEW_KEY);

  const isLiveView = restoreSavedView?.dataType === InsightCollectionDataType.live;

  useEffect(() => {
    setIsRestoringSavedView(!!savedView);
    if (!savedView && restoreLoading) {
      setRestoreLoading(false);
    }
  }, [savedView]);

  useEffect(() => {
    if (savedView && restoreLoading === false) {
      window.sessionStorage.removeItem(SAVED_VIEW_KEY);
    }
  }, [restoreLoading]);

  useRestoreSavedView((applyViewState: SavedViewType, pageView: Omit<PageView<object>, 'data'>) => {
    if (!restoreSavedView) {
      setRestoreSavedView({ ...applyViewState, dataType: pageView?.dataType as InsightCollectionDataType });
    }
  });

  const applyViewValues = async (
    filterValues: FiltersValues,
    settingsValues: SettingsValues,
    pageSettings: PageSettings
  ) => {
    const contextManager = ContextManager.getInstance();
    const context = contextManager.get();

    if (context.filters) {
      const clearAllFilters = clearValues(context.filters.values);
      contextManager.updateFilterValues({ ...clearAllFilters, ...filterValues });
    }

    const clearAllSettings = clearValues(context.settings.values);
    contextManager.updateSettingsValues({ ...clearAllSettings, ...settingsValues }, isLiveView);
    await PageSettingsManager.getInstance().set(page.name, pageSettings);
  };

  const applyViewV0 = async (view: UseRestoreSavedViewV0) => {
    const {
      context: {
        filters: { values: filterValues },
        settings: { values: settingValues },
      },
      pageSettings,
    } = view;

    await applyViewValues(filterValues, settingValues, pageSettings);
  };

  const applyViewV1 = async (view: UseRestoreSavedViewV1) => {
    const { filterValues, settingValues, pageSettings } = view;

    await applyViewValues(filterValues, settingValues, pageSettings);
  };

  const applyView = async () => {
    const viewVersion = restoreSavedView.version ?? SavedViewVersion.v0;
    switch (viewVersion) {
      case SavedViewVersion.v0:
        await applyViewV0(restoreSavedView as UseRestoreSavedViewV0);
        break;
      case SavedViewVersion.v1:
        await applyViewV1(restoreSavedView as UseRestoreSavedViewV1);
        break;
      default:
        throw new Error('Unknown view version');
    }
  };

  useEffect(() => {
    let timeoutId;

    if (contextLoading === false && restoreSavedView && !isRestoreSavedViewApplied.current) {
      clearTimeout(timeoutId);

      applyView()
        .catch(error => {
          console.error('Error in applyView:', error);
        })
        .finally(() => {
          setRestoreLoading(false);
          setIsRestoringSavedView(false);
        });
      isRestoreSavedViewApplied.current = true;
    } else {
      clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        setRestoreLoading(false);
      }, 5000);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [contextLoading, restoreSavedView]);

  return { isRestoringSavedView, restoreLoading, isLiveView };
};
